import { TApiResponse } from "@/types/api";
import { handleAsync } from "@/utils";

import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TInvestorInvite,
  TInviteCreatePayload,
  TJobPublicState,
  TPaginatedInvites,
  TPaginatedJobs,
  TPublicInvite,
  TSynopsisSubmitPayload,
  TVettingJobDetail,
  TVettingJobsQuery,
  TVettingStatusResponse,
} from "./investorVetting.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

// Public lookup runs before the user has an account — no auth hooks.
const publicApiService = new ApiService({});

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

class InvestorVettingService {
  private rootEndpoint = "investor-vetting" as const;
  private adminInvitesEndpoint = "admin/investor-invites" as const;

  // ---- Job pool (sales/admin) ----

  getJobs(query: TVettingJobsQuery = {}, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedJobs>>(() =>
      apiService.get<TApiResponse<TPaginatedJobs>>(`${this.rootEndpoint}/jobs${buildQuery(query)}`, options)
    );
  }

  getJob(jobId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TVettingJobDetail>>(() =>
      apiService.get<TApiResponse<TVettingJobDetail>>(`${this.rootEndpoint}/jobs/${jobId}`, options)
    );
  }

  claimJob(jobId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TVettingJobDetail>>(() =>
      apiService.post<TApiResponse<TVettingJobDetail>>(`${this.rootEndpoint}/jobs/${jobId}/claim`, undefined, options)
    );
  }

  releaseJob(jobId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TVettingJobDetail>>(() =>
      apiService.post<TApiResponse<TVettingJobDetail>>(`${this.rootEndpoint}/jobs/${jobId}/release`, undefined, options)
    );
  }

  onboardInvestor(jobId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TVettingJobDetail>>(() =>
      apiService.post<TApiResponse<TVettingJobDetail>>(`${this.rootEndpoint}/jobs/${jobId}/onboard`, undefined, options)
    );
  }

  declineInvestor(jobId: string, reason: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TVettingJobDetail>>(() =>
      apiService.post<TApiResponse<TVettingJobDetail>>(
        `${this.rootEndpoint}/jobs/${jobId}/decline`,
        { reason },
        options
      )
    );
  }

  // ---- Investor-facing ----

  submitSynopsis(payload: TSynopsisSubmitPayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TJobPublicState>>(() =>
      apiService.post<TApiResponse<TJobPublicState>>(`${this.rootEndpoint}/synopsis`, payload, options)
    );
  }

  getVettingStatus(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TVettingStatusResponse>>(() =>
      apiService.get<TApiResponse<TVettingStatusResponse>>(`${this.rootEndpoint}/status`, options)
    );
  }

  // ---- Invitations (admin) ----

  createInvite(payload: TInviteCreatePayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorInvite>>(() =>
      apiService.post<TApiResponse<TInvestorInvite>>(this.adminInvitesEndpoint, payload, options)
    );
  }

  getInvites(query: { status?: string; limit?: number; offset?: number } = {}, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TPaginatedInvites>>(() =>
      apiService.get<TApiResponse<TPaginatedInvites>>(`${this.adminInvitesEndpoint}${buildQuery(query)}`, options)
    );
  }

  /** Re-sends the SAME token; never extends expiry (RD-4). */
  resendInvite(inviteId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorInvite>>(() =>
      apiService.post<TApiResponse<TInvestorInvite>>(
        `${this.adminInvitesEndpoint}/${inviteId}/resend`,
        undefined,
        options
      )
    );
  }

  /** Fresh token + window; only for expired/revoked invites (RD-4). */
  recreateInvite(inviteId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorInvite>>(() =>
      apiService.post<TApiResponse<TInvestorInvite>>(
        `${this.adminInvitesEndpoint}/${inviteId}/recreate`,
        undefined,
        options
      )
    );
  }

  revokeInvite(inviteId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorInvite>>(() =>
      apiService.delete<TApiResponse<TInvestorInvite>>(`${this.adminInvitesEndpoint}/${inviteId}`, options)
    );
  }

  /** RD-17: grant trusted investor access to an EXISTING user. */
  grantInvestorAccess(userId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TJobPublicState>>(() =>
      apiService.post<TApiResponse<TJobPublicState>>("admin/investor-vetting/grant", { user_id: userId }, options)
    );
  }

  // ---- Public invite lookup (unauthenticated) ----

  getInviteByToken(token: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TPublicInvite>>(() =>
      publicApiService.get<TApiResponse<TPublicInvite>>(`investor-invites/${encodeURIComponent(token)}`, options)
    );
  }
}

export const investorVettingService = new InvestorVettingService();
